import axios, { AxiosError } from "axios";
import type { AxiosRequestConfig } from "axios";

export const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_BACKEND_URL || "http://localhost:3000",
  withCredentials: true,
});

let isRefreshing = false;
let failedQueue: {
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Configure the axios interceptor with app-specific store and logout action.
 * Must be called once at app startup (e.g., in main.tsx) before any API calls.
 *
 * @param getStore - Function that returns the Redux store instance
 * @param logoutAction - The Redux action creator for logout
 */
export function configureAxiosInterceptor(
  getStore: () => { dispatch: (action: unknown) => void },
  logoutAction: () => unknown,
) {
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as AxiosRequestConfig & {
        _retry?: boolean;
      };

      const status = error.response?.status;

      // 403 on a non-auth endpoint means the session is no longer valid
      // (e.g. RolesGuard rejects because token payload is stale/missing role).
      // Logout immediately without attempting refresh.
      if (
        status === 403 &&
        !originalRequest.url?.includes("/auth/login")
      ) {
        getStore().dispatch(logoutAction());
        return Promise.reject(error);
      }

      if (
        status === 401 &&
        !originalRequest._retry &&
        !originalRequest.url?.includes("/auth/refresh") &&
        !originalRequest.url?.includes("/auth/login")
      ) {
        originalRequest._retry = true;

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(() => axiosInstance(originalRequest));
        }

        isRefreshing = true;

        try {
          await axiosInstance.post("/auth/refresh");
          processQueue(null);
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          getStore().dispatch(logoutAction());
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    },
  );
}
